'use client'

import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import SafeImage from '@/components/SafeImage'
import type { ProductImage } from '@/data/products/types'

type ProductGalleryProps = {
  images: ProductImage[]
  productName: string
}

export default function ProductGallery({
  images,
  productName,
}: ProductGalleryProps) {
  const [active, setActive] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const current = images[active] ?? images[0]

  const openAt = (index: number) => {
    setActive(index)
    setLightboxOpen(true)
  }

  const go = useCallback(
    (dir: -1 | 1) => {
      setActive((prev) => {
        const len = images.length
        if (len < 2) return prev
        return (prev + dir + len) % len
      })
    },
    [images.length]
  )

  useEffect(() => {
    if (!lightboxOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxOpen(false)
      if (e.key === 'ArrowLeft') go(-1)
      if (e.key === 'ArrowRight') go(1)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [lightboxOpen, go])

  if (!current) return null

  const lightboxImage = images[active] ?? current

  return (
    <div>
      <button
        type="button"
        className="pp-gallery-main w-full"
        onClick={() => openAt(active)}
        aria-label={`${productName} görselini büyüt`}
      >
        <SafeImage
          src={current.src}
          alt={current.alt}
          fill
          sizes="(max-width: 992px) 100vw, 480px"
          className="object-cover"
          priority
        />
      </button>

      <div className="pp-thumbs" role="list">
        {images.map((img, index) => (
          <button
            key={`${img.src}-${index}`}
            type="button"
            role="listitem"
            className={`pp-thumb${index === active ? ' is-active' : ''}`}
            onClick={() => openAt(index)}
            aria-label={`${productName} görsel ${index + 1}`}
            aria-current={index === active ? 'true' : undefined}
          >
            <SafeImage
              src={img.src}
              alt={img.alt}
              fill
              sizes="96px"
              className="object-cover"
            />
          </button>
        ))}
      </div>

      {lightboxOpen ? (
        <div
          className="pp-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Ürün görseli"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            type="button"
            className="pp-lightbox-close"
            aria-label="Kapat"
            onClick={(e) => {
              e.stopPropagation()
              setLightboxOpen(false)
            }}
          >
            <X size={20} />
          </button>

          {images.length > 1 ? (
            <>
              <button
                type="button"
                className="pp-lightbox-nav pp-lightbox-prev"
                aria-label="Önceki görsel"
                onClick={(e) => {
                  e.stopPropagation()
                  go(-1)
                }}
              >
                <ChevronLeft size={26} />
              </button>
              <button
                type="button"
                className="pp-lightbox-nav pp-lightbox-next"
                aria-label="Sonraki görsel"
                onClick={(e) => {
                  e.stopPropagation()
                  go(1)
                }}
              >
                <ChevronRight size={26} />
              </button>
            </>
          ) : null}

          <div
            className="pp-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pp-lightbox-frame">
              <SafeImage
                key={lightboxImage.src}
                src={lightboxImage.src}
                alt={lightboxImage.alt}
                fill
                sizes="100vw"
                className="object-contain"
              />
            </div>
            <p className="pp-lightbox-count">
              {active + 1} / {images.length}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  )
}
